import { captureBookmarklet, captureLink, captureNoteBody, normalizeCapture, readCaptureFragment, type BrowserCapture } from "./browser-capture.ts";
import { capturePreviewView, captureSetupView } from "./browser-capture-view.ts";

interface Capabilities {
  document: Document;
  applicationUrl(): string;
  personalRoot(): string | null;
  accountLabel(): string;
  now(): string;
  randomId(): string;
  create(id: string, rootId: string, body: string, baseBody: string): Promise<void>;
  remove(id: string, rootId: string, baseBody: string): Promise<void>;
  saved(): void;
  openNote(id: string): Promise<boolean>;
  hash(): string;
  clearHash(): void;
  copy(text: string): Promise<void>;
  toast(message: string, error?: boolean): void;
}

export function createBrowserCaptureController(c: Capabilities) {
  let dialog: HTMLDialogElement | null = null;
  let busy = false;
  let returnFocus: HTMLElement | null = null;
  const status = (message: string) => { const region = dialog?.querySelector("[data-capture-status]"); if (region) region.textContent = message; };
  const dismiss = () => { if (!busy) dialog?.close(); };

  function mount(label: string, html: string): HTMLDialogElement {
    if (!returnFocus) {
      const active = c.document.activeElement as HTMLElement | null;
      returnFocus = active?.closest("details")?.querySelector<HTMLElement>("summary") || active;
    }
    const previous = dialog;
    const element = c.document.createElement("dialog");
    dialog = element;
    previous?.close();
    element.className = "browser-capture-dialog";
    element.setAttribute("aria-label", label);
    element.innerHTML = html;
    element.addEventListener("keydown", event => event.stopPropagation());
    element.addEventListener("cancel", event => { if (busy) event.preventDefault(); });
    element.addEventListener("click", event => { if (event.target === element) dismiss(); });
    element.querySelectorAll("[data-capture-cancel]").forEach(button => button.addEventListener("click", dismiss));
    element.addEventListener("close", () => {
      element.remove();
      if (dialog !== element) return;
      dialog = null;
      (returnFocus?.isConnected ? returnFocus : c.document.querySelector<HTMLElement>(".quick-note-menu > summary"))?.focus({ preventScroll: true });
      returnFocus = null;
    });
    c.document.body.append(element);
    element.showModal();
    return element;
  }

  function preview(initial: Partial<BrowserCapture> = {}): void {
    if (busy) return;
    const rootId = c.personalRoot();
    if (!rootId) { c.toast("Sign in before capturing a private note.", true); return; }
    const element = mount("Capture to Quick Notes", capturePreviewView());
    const id = c.randomId();
    let saved = false;
    let savedBody = "";
    const input = (name: string) => element.querySelector<HTMLInputElement | HTMLTextAreaElement>(`[name="${name}"]`)!;
    input("captureTitle").value = initial.title || "";
    input("captureUrl").value = initial.url || "";
    input("captureText").value = initial.text || "";
    input("captureDate").value = initial.capturedAt || c.now();
    input("captureBy").value = initial.attribution || "";
    element.querySelector("[data-capture-account]")!.textContent = `Private to ${c.accountLabel()}. Saved locally first, then synchronized to your account.`;
    element.querySelector<HTMLElement>("[data-capture-shortened]")!.hidden = !initial.truncated;
    const value = () => normalizeCapture({ title: input("captureTitle").value, url: input("captureUrl").value, text: input("captureText").value, capturedAt: input("captureDate").value, attribution: input("captureBy").value, truncated: initial.truncated }, c.now());
    const checkAccount = () => { if (c.personalRoot() !== rootId) throw new Error("Your account changed. Close this preview and capture again."); };
    const refresh = () => {
      try { element.querySelector("pre")!.textContent = captureNoteBody(value()); status(""); }
      catch (error) { element.querySelector("pre")!.textContent = ""; status(error instanceof Error ? error.message : "Review the capture fields."); }
    };
    const controls = (locked: boolean) => element.querySelectorAll<HTMLInputElement | HTMLButtonElement | HTMLTextAreaElement>("input,textarea,button").forEach(control => { control.disabled = locked; });
    const showSaved = () => {
      element.querySelector<HTMLElement>("[data-capture-unsaved]")!.hidden = saved;
      element.querySelector<HTMLElement>("[data-capture-saved]")!.hidden = !saved;
      element.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>("input,textarea").forEach(control => { control.disabled = saved; });
    };
    element.querySelector("form")!.addEventListener("input", refresh);
    element.querySelector("form")!.addEventListener("submit", event => {
      event.preventDefault();
      if (busy || saved) return;
      void (async () => {
        busy = true; controls(true);
        try {
          checkAccount();
          const body = captureNoteBody(value());
          await c.create(id, rootId, body, savedBody);
          savedBody = body;
          saved = true;
          c.saved();
          status("Saved on this device and queued to synchronize. You can open the note, convert it to a task from its actions, or undo this save.");
        } catch (error) { status(error instanceof Error ? error.message : "Could not save. Retry keeps the same note, not a duplicate."); }
        finally { busy = false; controls(false); showSaved(); element.querySelector<HTMLElement>(saved ? "[data-capture-open]" : '[type="submit"]')?.focus(); }
      })();
    });
    element.querySelector("[data-capture-undo]")!.addEventListener("click", () => {
      if (busy || !saved) return;
      void (async () => {
        busy = true; controls(true);
        try { checkAccount(); await c.remove(id, rootId, savedBody); saved = false; c.saved(); status("Save undone. The original page is unchanged; you can edit and save again."); }
        catch (error) { status(error instanceof Error ? error.message : "Could not undo. Try again."); }
        finally { busy = false; controls(false); showSaved(); }
      })();
    });
    element.querySelector("[data-capture-open]")!.addEventListener("click", () => {
      if (busy || !saved) return;
      void (async () => {
        busy = true; controls(true);
        try { checkAccount(); if (!await c.openNote(id)) throw new Error("Could not open the saved note. It is still saved; retry after resolving any unsaved draft."); busy = false; element.close(); }
        catch (error) { status(error instanceof Error ? error.message : "Could not open the saved note."); }
        finally { busy = false; controls(false); showSaved(); }
      })();
    });
    refresh(); input("captureTitle").focus();
  }

  function setup(): void {
    if (busy || dialog) return;
    const element = mount("Browser capture", captureSetupView());
    const bookmark = element.querySelector<HTMLAnchorElement>("[data-capture-bookmark]")!;
    bookmark.href = captureBookmarklet(c.applicationUrl());
    bookmark.addEventListener("click", event => { event.preventDefault(); status("Drag the link to your bookmarks bar; activate it on the page you want to capture. You can also use Capture a link below."); });
    const link = captureLink(c.applicationUrl());
    element.querySelector<HTMLInputElement>("[data-capture-link]")!.value = link;
    element.querySelector("[data-capture-copy]")!.addEventListener("click", () => {
      void c.copy(link).then(() => { status("Capture link copied. It contains no page content or account credentials."); }).catch(() => { const input = element.querySelector<HTMLInputElement>("[data-capture-link]")!; input.focus(); input.select(); status("Copy the selected link using your browser’s copy action."); });
    });
    element.querySelector("[data-capture-manual]")!.addEventListener("click", () => preview());
    element.querySelector<HTMLElement>("[data-capture-cancel]")?.focus();
  }

  async function resume(): Promise<boolean> {
    if (!c.personalRoot() || dialog || busy) return false;
    try {
      const capture = readCaptureFragment(c.hash(), c.now());
      if (!capture) return false;
      c.clearHash();
      preview(capture);
      return true;
    } catch (error) {
      c.clearHash();
      c.toast(error instanceof Error ? error.message : "This capture link is invalid. Use Browser capture to paste the content manually.", true);
      return true;
    }
  }

  c.document.addEventListener("click", event => {
    const target = (event.target as Element | null)?.closest("[data-browser-capture]");
    if (!target) return;
    event.preventDefault(); target.closest("details")?.removeAttribute("open"); setup();
  });
  return { setup, preview, resume };
}
